import { FC, ReactNode, useState, useRef } from 'react'
import { useDemoConversationStore } from '@forwardflow/store/demoConversationStore'
import { message } from 'antd'
import React from 'react'
import { useAuth } from '@forwardflow/context/AuthContext'

interface CtrlClickSaveWrapperProps {
  children: ReactNode
  leadId: number
  stepIndex: number
  navigationMode: 'campaign' | 'predefined'
  className?: string
}

export const CtrlClickSaveWrapper: FC<CtrlClickSaveWrapperProps> = ({
  children,
  leadId,
  stepIndex,
  navigationMode,
  className = ''
}) => {
  const {
    isSaving,
    getPreDefinedTimelineHistory,
    getTimelineHistory,
    saveTimelineUpToStep,
  } = useDemoConversationStore()

  const { auth: { user } } = useAuth()

  const hasPermission = user?.email === 'operator@forward-flow.com' ||
    user?.email === 'brannon@forward-flow.com' ||
    user?.email === 'engineered@forward-flow.com'

  const [showSaveIndicator, setShowSaveIndicator] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const saveKey = `save-step-${leadId}-${stepIndex}`
  const isLoading = isSaving(saveKey)

  const isStepSaved = (() => {
    const predefinedHistory = getPreDefinedTimelineHistory(leadId)
    const currentHistory = getTimelineHistory(leadId)

    if (stepIndex >= predefinedHistory.length) return false
    if (stepIndex >= currentHistory.length) return false

    const currentStep = currentHistory[stepIndex]
    const predefinedStep = predefinedHistory[stepIndex]

    return JSON.stringify(currentStep) === JSON.stringify(predefinedStep)
  })()

  const canSave = hasPermission && (navigationMode === 'campaign' ||
    (navigationMode === 'predefined' && !isStepSaved))

  const handleClick = async (event: React.MouseEvent) => {
    if (!canSave || !(event.ctrlKey || event.metaKey)) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    try {
      const success = await saveTimelineUpToStep(leadId, stepIndex)

      if (success) {
        setSaveSuccess(true)

        const currentTimeline = getTimelineHistory(leadId)
        const stepData = currentTimeline[stepIndex]

        if (stepData?.isActiveConversationItem) {
          message.success('Timeline saved Successfully')
        } else {
          message.success('Step saved successfully!')
        }

        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
          setSaveSuccess(false)
        }, 2000)
      } else {
        message.error('Failed to save step. Please try again.')
      }
    } catch (error) {
      console.error('Save error:', error)
      message.error('An error occurred while saving.')
    }
  }

  const handleMouseEnter = () => {
    if (canSave) {
      setShowSaveIndicator(true)
    }
  }

  const handleMouseLeave = () => {
    setShowSaveIndicator(false)
  }

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <div
      className={`relative cursor-pointer ${className}`}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      title={canSave ? "Ctrl+Click to save this step" : undefined}
    >
      {children}

      {canSave && (showSaveIndicator || isLoading || saveSuccess) && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-2 right-2 flex items-center justify-center">
            {isLoading ? (
              <div className="w-6 h-6 rounded-full bg-blue-500/20 border border-blue-400/40 flex items-center justify-center">
                <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : saveSuccess ? (
              <div className="w-6 h-6 rounded-full bg-green-500/20 border border-green-400/40 flex items-center justify-center animate-pulse">
                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}